/**
 * Board Layout Store
 * @xstate/store for board layout data (dayWidth, rowHeight, headerHeight, etc.)
 * Provides granular subscriptions via useSelector.
 * Extended with sidebar, header config, and detail level state.
 */

import { createStore } from '@xstate/store';
import type { Task } from '../../types';
import type { TaskDetailLevel } from '../hooks/useBoardTaskDetailLevel';
import type { HeaderRowId } from '../hooks/useBoardHeaderResize';

export interface BoardLayoutContext {
  dayWidth: number;
  rowHeight: number;
  headerHeight: number;
  columnYOffsets: Record<string, number>;
  rowAssignments: Record<string, Map<string, number>>;
  tasksByColumn: Record<string, Task[]>;
  totalHeight: number;
  totalDays: number;
  visibleWeeks: { kw: number; year: number; startDay: number; weekIndex: number }[];

  // Sidebar
  sidebarWidth: number;
  isCollapsed: boolean;
  isSidebarResizing: boolean;

  // Detail
  detailLevel: TaskDetailLevel;
  isDetailResizing: boolean;

  // Header config
  visibleRows: HeaderRowId[];
  isHeaderResizing: boolean;
  headerMode: 'auto' | 'manual';
}

export function createBoardLayoutStore(initial: BoardLayoutContext) {
  return createStore({
    context: initial,
    on: {
      'layout.update': (_ctx, event: Partial<BoardLayoutContext>) => ({ ..._ctx, ...event }),
      'sidebar.update': (_ctx, event: Partial<Pick<BoardLayoutContext, 'sidebarWidth' | 'isCollapsed' | 'isSidebarResizing'>>) => ({ ..._ctx, ...event }),
      'detail.update': (_ctx, event: Partial<Pick<BoardLayoutContext, 'detailLevel' | 'isDetailResizing' | 'rowHeight'>>) => ({ ..._ctx, ...event }),
      'header.config.update': (_ctx, event: Partial<Pick<BoardLayoutContext, 'visibleRows' | 'isHeaderResizing' | 'headerMode' | 'headerHeight'>>) => ({ ..._ctx, ...event }),
    },
  });
}

export type BoardLayoutStore = ReturnType<typeof createBoardLayoutStore>;
